import { getReferenceLabel } from 'reporting-data/v2/dataset/datasetRecords';
import I18n from 'I18n';
// @ts-expect-error untyped dependency
import { getLegendPositionOptions } from '../../highcharts/options/legendOptions';
import { blendColors } from './VennUtils';
import { fromJS, List } from 'immutable';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { getPrimaryDataDataset, getPrimaryDataset } from 'reporting-data/v2/dataset/datasetGetters';
// Converts raw sets data (List or single value) to array format for consistent processing
// single value Ids from backend come as string while intersections come as array
const convertSetsToArray = rawSets => {
  return List.isList(rawSets) ? rawSets.toArray() : [rawSets];
};

// Creates human-readable names for venn segments - single value Ids are used as is, intersections are converted to human readable names
const generateVennSegmentName = (sets, primaryDataset, setsColumn) => {
  if (sets.length === 1) {
    return getReferenceLabel(primaryDataset, setsColumn, sets[0], sets[0]);
  } else {
    const setNames = sets.map(set => getReferenceLabel(primaryDataset, setsColumn, set, set));
    if (setNames.length === 2) {
      return I18n.text('reporting-visualizations.venn.twoSets', {
        first: setNames[0],
        second: setNames[1]
      });
    } else {
      const last = setNames[setNames.length - 1];
      const allButLast = setNames.slice(0, -1).join(', ');
      return I18n.text('reporting-visualizations.venn.multipleSets', {
        allButLast,
        last
      });
    }
  }
};

// Calculates which list IDs are NOT in the current intersection (for drilldown logic)
const calculateExclusiveListIds = (sets, allListIds) => {
  return sets.length === 1 ? [] : allListIds.toArray().map(listId => String(listId)).filter(listId => !sets.includes(listId));
};

// Creates click event handler for venn segments to enable drilldown functionality
const createVennDataPointClickHandler = (onPointClick, layer, vennData) => {
  return event => {
    const {
      point
    } = event;
    const drilldownPayload = {
      point: fromJS({
        inclusiveListIds: point.inclusiveListIds,
        exclusiveListIds: point.exclusiveListIds,
        objectTypeId: point.objectTypeId,
        sets: point.sets,
        value: point.value,
        name: point.name
      }),
      layer: fromJS({
        dataset: 'primary',
        setsColumn: layer.get('setsColumn'),
        dataColumn: layer.get('dataColumn')
      }),
      dataset: fromJS({
        key: 'primary',
        data: vennData
      }),
      levelOfDetail: point.sets || []
    };
    onPointClick(drilldownPayload);
  };
};

// Creates data label configuration for highcharts (shows names for single sets, values for intersections)
const createVennDataLabels = style => {
  var _style$get;
  return {
    enabled: (style === null || style === void 0 || (_style$get = style.get('dataLabels')) === null || _style$get === void 0 ? void 0 : _style$get.get('enabled')) !== false,
    style: {
      textOverflow: 'ellipsis'
    },
    formatter() {
      const point = this.point;
      const value = point.value || 0;
      const name = point.name || '';
      return point.sets.length === 1 ? `${name}` : `${value.toString()}`;
    }
  };
};

// Builds final Highcharts configuration object with data, legend, and chart options
const buildVennChartOptions = (highchartsData, style) => {
  const {
    legend: legendOptions = {},
    chart: legendOffsets = {}
  } = getLegendPositionOptions(style === null || style === void 0 ? void 0 : style.get('legendPosition'), style === null || style === void 0 ? void 0 : style.get('legendWidth'));
  return {
    type: 'venn',
    data: highchartsData,
    dataLabels: (style === null || style === void 0 ? void 0 : style.get('dataLabels')) !== undefined ? style.get('dataLabels') : true,
    tooltipDisabled: style === null || style === void 0 ? void 0 : style.get('tooltipDisabled'),
    chart: Object.assign({}, legendOffsets),
    legend: Object.assign({
      enabled: true
    }, legendOptions)
  };
};

// Converts raw dataset rows into structured VennDataPoint objects with names, colors, click handlers, and drilldown metadata
const transformVennDataToDataPoints = (vennData, layer, primaryDataset, style, onPointClick) => {
  const setsColumn = layer.get('setsColumn');
  const dataColumn = layer.get('dataColumn');
  const allListIds = layer.get('listIds') || List([]);
  const colors = style.getIn(['colors', 'defined']);
  return vennData.map(item => {
    const rawSets = item.get(setsColumn) || [];
    const sets = convertSetsToArray(rawSets);
    const objectTypeId = item.get('object_type_id');
    const value = item.get(dataColumn);
    const name = generateVennSegmentName(sets, primaryDataset, setsColumn);
    const inclusiveListIds = sets;
    const exclusiveListIds = calculateExclusiveListIds(sets, allListIds);
    const vennDataPoint = {
      sets,
      value,
      name,
      objectTypeId,
      inclusiveListIds,
      exclusiveListIds,
      color: sets.length === 1 ? colors.get(sets[0]) : blendColors(...sets.map(set => colors.get(set))),
      dataLabels: createVennDataLabels(style)
    };
    if (onPointClick) {
      vennDataPoint.events = {
        click: createVennDataPointClickHandler(onPointClick, layer, vennData)
      };
    }
    return vennDataPoint;
  }).toArray();
};

// Main function that orchestrates the entire mapping process from VennVisualizationRecord to Highcharts options
export const getVennOptions = (visualization, debug = DEFAULT_DEBUG) => {
  const data = visualization.get('data');
  const primaryDataset = getPrimaryDataset(data);
  const vennData = getPrimaryDataDataset(data);
  const layer = visualization.get('layer');
  const events = visualization.get('events');
  const {
    onPointClick
  } = events !== null && events !== void 0 ? events : {};
  const style = visualization.get('style');
  const highchartsData = transformVennDataToDataPoints(vennData, layer, primaryDataset, style, onPointClick);
  const options = buildVennChartOptions(highchartsData, style);
  debug.set('Venn Chart', options);
  return options;
};